/**
 * useImageEditing Hook
 *
 * State management for AI Image Editing feature.
 * Handles image + text instruction → edited image generation.
 */

import { useState, useCallback } from 'react';
import { imageEditingApi, type ImageEditRequest, type ImageEditJob } from '@/services/imageEditing';

import { handleError } from '../utils/errorHandler';

export interface ImageEditingState {
  isEditing: boolean;
  progress: number;
  message: string;
  error: string | null;
  resultUrl: string | null;
  cost: number;
  tokens: number;
}

const initialState: ImageEditingState = {
  isEditing: false,
  progress: 0,
  message: '',
  error: null,
  resultUrl: null,
  cost: 0,
  tokens: 0,
};

export function useImageEditing() {
  const [state, setState] = useState<ImageEditingState>(initialState);

  const edit = useCallback(async (request: ImageEditRequest) => {
    setState({
      ...initialState,
      isEditing: true,
      message: 'Starting image editing...',
    });

    try {
      // Start editing - capture estimated_tokens from the initial response
      const job = await imageEditingApi.edit(request);
      const billedTokens = job.estimated_tokens || 0;

      // Poll for completion
      const finalJob = await imageEditingApi.waitForCompletion(
        job.job_id,
        (progressJob: ImageEditJob) => {
          setState((prev) => ({
            ...prev,
            progress: progressJob.progress || 0,
            message: progressJob.message || 'Processing...',
          }));
        }
      );

      if (finalJob.status === 'completed' && finalJob.result_url) {
        setState((prev) => ({
          ...prev,
          isEditing: false,
          progress: 100,
          message: 'Edit complete!',
          resultUrl: finalJob.result_url || null,
          cost: finalJob.cost,
          tokens: billedTokens,
          error: null,
        }));
      } else {
        throw new Error(finalJob.error || 'Editing failed');
      }
    } catch (err) {
      const errorMessage = handleError(err, 'ImageEditing', 'An unexpected error occurred');
      setState((prev) => ({
        ...prev,
        isEditing: false,
        error: errorMessage,
        message: '',
      }));
    }
  }, []);

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  return {
    ...state,
    edit,
    reset,
  };
}
